import {FormFieldValidationResult} from '@mintdata-wsp/common/components/form/Form';
import {ApiError} from '@mintdata-wsp/common/controllers/network/Fetcher';
import {InviteUserRequest} from '@mintdata-wsp/common/controllers/network/generated/requests/InviteUserRequest';
import {CheckResponse} from '@mintdata-wsp/common/controllers/network/generated/responses/CheckResponse';
import {DeleteInvitationResult} from '@mintdata-wsp/common/controllers/network/generated/responses/DeleteInvitationResult';
import {ExtendedUserGroupsResponse} from '@mintdata-wsp/common/controllers/network/generated/responses/ExtendedUserGroupsResponse';
import {InvitationDetailsResponse} from '@mintdata-wsp/common/controllers/network/generated/responses/InvitationDetailsResponse';
import {InviteCreationResponse} from '@mintdata-wsp/common/controllers/network/generated/responses/InviteCreationResponse';
import {NoExpectedResponse} from '@mintdata-wsp/common/controllers/network/generated/WspAPI';
import {rootState} from '@mintdata-wsp/common/state/RootState';
import {MemberRole, OrganizationMember} from '@mintdata-wsp/common/state/UIState';
import {API} from '@mintdata-wsp/common/controllers/network/WorkspaceAPI';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import bind from 'bind-decorator';
import {action, reaction} from 'mobx';

export class MembersController
{
    @bind
    public init()
    {
        this._setReactions();
    }

    @bind
    private _setReactions()
    {
        reaction(() =>
            {
                return controllers.user.getUserId();
            },
            () =>
            {
                this._setMembersList();
            });
        // reaction(() => { return controllers.user.getUserRoles()},
        //     () => {
        //         this._setMembersList();
        //     });
        reaction(() =>
            {
                return rootState.ui.members.groups;
            },
            () =>
            {
                this._setMembersList();
            });
        reaction(() =>
            {
                return rootState.ui.members.invitations;
            },
            () =>
            {
                this._setMembersList();
            });
    }

    public syncOrganizationUsers():void
    {
        this._syncUser();

        API.userGroups.extended().then(action((response:ExtendedUserGroupsResponse) =>
        {
            if( ApiError.isError(response) )
                throw response;

            rootState.ui.members.groups = response.groups;
        }));
    }

    public syncOrganizationPendingUsers():void
    {
        this._syncUser();

        API.invite.getInvitationsForOrganizations()
            .then(action((response:InvitationDetailsResponse) =>
            {
                if( ApiError.isError(response) )
                    throw response;

                rootState.ui.members.invitations = response.invitations;
            }))
            .catch(err =>
            {
                console.warn(err);
            });
    }

    private _syncUser():void
    {
        if( !controllers.user.getUserId() )
            controllers.user.syncUserId();

        // if (!controllers.user.getUserRoles())
        //     controllers.user.syncUserRoles();
    }

    private _setMembersList():void
    {
        const myId = controllers.user.getUserId();
        if( !myId ) return;

        // const myRoles = controllers.user.getUserRoles();
        // if (!myRoles) return;

        const {groups, invitations} = rootState.ui.members;
        if( !groups && !invitations ) return;

        let members = [];

        // const myRole = MemberRole[myRoles[0]];
        let myRole:MemberRole = MemberRole.Members;

        if( groups )
        {
            groups.forEach(group =>
            {
                group.users.forEach(user =>
                {
                    if( user.id == myId )
                        myRole = group.roles[0].id;
                });
            });

            groups.forEach(group =>
            {
                group.users.forEach(user =>
                {
                    members.push(new OrganizationMember(
                        user.id,
                        user.name,
                        group.roles[0].id,
                        group.id,
                        user.id == myId,
                        myRole,
                        user.avatarUrl
                    ));
                });
            });
        }

        if( invitations )
        {
            invitations.forEach(invitation =>
            {
                members.push(new OrganizationMember(
                    invitation.id,
                    invitation.email,
                    MemberRole.Pending,
                    -1,// group.id,
                    false,
                    myRole
                ));
            });
        }

        rootState.ui.members.list = members;
    };

    public removeMember(member:OrganizationMember):Promise<NoExpectedResponse | DeleteInvitationResult>
    {
        if( member.role == MemberRole.Pending )
            return this._deleteUserInvitation(member);

        return this._removeUsers(member);
    }

    private _deleteUserInvitation(member:OrganizationMember)
    {
        return API.invite.deleteUserInvitation(member.id)
            .then(action((response:DeleteInvitationResult | ApiError) =>
            {
                if( ApiError.isError(response) )
                {
                    console.warn('API.userGroups.removeUsers ApiError =', response);
                    return response;
                }

                if( (response as DeleteInvitationResult).isSuccessful )
                {
                    let {invitations} = rootState.ui.members;

                    const removedUserIndex:number = invitations.reduce((foundIndex, user, i) => user.id == member.id ? i : foundIndex, -1);

                    if( removedUserIndex != -1 )
                        invitations.splice(removedUserIndex, 1);

                    rootState.ui.members.invitations = [...invitations];
                    return response;
                }
                else
                {
                    return new ApiError(777, (response as DeleteInvitationResult).errorMessage, null);
                }
            }));
    }

    private _removeUsers(member:OrganizationMember)
    {
        return API.userGroups.removeUsers(member.groupId, {userIds: [member.id]})
            .then(action((response) =>
            {
                if( ApiError.isError(response) )
                {
                    console.warn('API.userGroups.removeUsers ApiError =', response);
                    return response;
                }

                let {groups} = rootState.ui.members;

                let group = groups.filter(group => group.id == member.groupId)[0];

                const removedUserIndex:number = group.users.reduce((foundIndex, user, i) => user.id == member.id ? i : foundIndex, -1);

                if( removedUserIndex != -1 )
                    group.users.splice(removedUserIndex, 1);

                // TODO animation
                // setTimeout(action(() =>
                // {
                rootState.ui.members.groups = [...groups];
                // }
                // ), 300);

                return response;
            }));
    }

    @action
    public createInvitation(emails:string[])
    {
        const _emails = emails.map(email => new InviteUserRequest(email));

        rootState.ui.members.isInviteInProgress = true;

        return API.invite.createInvitation(_emails)
            .then(action((response:InviteCreationResponse | ApiError) =>
            {
                if( ApiError.isError(response) )
                {
                    throw (response as ApiError).message;
                }

                rootState.ui.members.isInviteInProgress = false;
            }))
            .catch(action((err) =>
            {
                console.warn(err);
                rootState.ui.members.isInviteInProgress = false;
            }));
    }

    public resendInvitation(id:number)
    {
        return API.invite.resendInvitation(id);
        // .then( (response:InviteCreationResponse | ApiError) =>
        // {
        //     // if (ApiError.isError(response))
        //     // {
        //     //     throw (response as ApiError).message;
        //     // }
        //
        //     return response;
        // })
        // // .catch((err) => { console.warn(err) });
    }

    public validateEmail(email:string):Promise<FormFieldValidationResult>
    {
        return API.profile.checkEmail(email as any)
            .then((response:CheckResponse | ApiError) =>
            {
                if( ApiError.isError(response) )
                {
                    return FormFieldValidationResult.invalidResult((response as ApiError).message);
                }

                // TODO take message only from server
                const errorMessage = (response as CheckResponse).allowed
                    ? ''
                    : (response as CheckResponse).message || 'Email not allowed';

                return new FormFieldValidationResult((response as CheckResponse).allowed, errorMessage);
            });
    }
}
